<template>
    <div v-if="!isDraft && (!event || !event.coverImageUrl)" class="loader content">
        <DLoader></DLoader>
    </div>
    <div v-else class="event">
        <div class="content">
            <div class="sub-nav">
                <div class="sub-nav-left">
                    <Button
                        :value="$t('event.back_to_overview')"
                        icon="arrow-left"
                        type="link"
                        @click="$router.go(-1)"
                    />
                </div>
                <div class="sub-nav-right">
                    <Button
                        v-if="event.status === 'Canceled'"
                        :value="$t('event.canceled')"
                        icon="x"
                        type="secondary"
                        round
                    />
                    <Button
                        v-if="event.status === 'Published'"
                        :value="$t('event.unpublish')"
                        icon="arrow-down-right"
                        type="secondary"
                        round
                        @click="unpublishEvent"
                    />
                    <Button
                        v-if="event.status === 'Draft'"
                        :value="$t('event.publish')"
                        icon="arrow-up-right"
                        type="secondary"
                        round
                        @click="publishEvent"
                    />
                    <Button v-if="false" :value="$t('event.save')" type="primary" round />
                    <Button v-if="isDraft" :value="$t('event.create')" type="primary" round @click="createEvent" />

                    <div v-if="!isDraft" class="more-horizontal">
                        <DContextMenu>
                            <DContextMenuItem
                                v-if="!isDraft && event.status !== 'Published'"
                                icon="eye"
                                :label="$t('event.publish')"
                                type="default"
                                @click="publishEvent"
                            />
                            <DContextMenuItem
                                v-if="!isDraft && event.status !== 'Draft'"
                                icon="eye-off"
                                :label="$t('event.unpublish')"
                                type="default"
                                @click="unpublishEvent"
                            />
                            <DContextMenuItem
                                v-if="!isDraft && event.status !== 'Canceled'"
                                icon="x"
                                :label="$t('event.cancel')"
                                type="default"
                                @click="cancelEvent"
                            />
                            <DContextMenuItem
                                v-if="!isDraft"
                                icon="trash-2"
                                :label="$t('event.delete')"
                                type="warning"
                            />
                        </DContextMenu>
                    </div>
                </div>
            </div>
            <div v-if="false" class="draft-banner">
                <Icon name="draft" />
                <div class="text-container">
                    <p class="copy-m bold">{{ $t('event.draft_banner.event_is_draft') }}</p>
                    <p class="copy-s">{{ $t('event.draft_banner.only_visible_after_publish') }}</p>
                </div>
            </div>
            <FeatureImage :src="event.coverImageUrl" @image="(e) => saveImage(e)" />
            <div class="main-content">
                <div class="settings-container">
                    <div class="settings-group">
                        <div class="event-name-container">
                            <div class="event-name">
                                <h2 v-if="event.name" class="bold">{{ event.name }}</h2>
                                <h2 v-else class="bold">Mein Event</h2>
                                <div class="rsvp-container">
                                    <FeatherIcon class="user-icon" name="users" style="margin-right: 0.8rem" />
                                    <div class="text copy-m">
                                        {{ event.sourceAttendingCount }} {{ $t('event.attending') }}
                                        {{ event.sourceInterestedCount }} {{ $t('event.are_interested') }}
                                    </div>
                                </div>
                            </div>
                            <div class="edit copy-s bold" @click="modalOpen = 'name'">{{ $t('event.edit') }}</div>
                        </div>
                        <div class="event-desc-container">
                            <div class="label-container">
                                <div class="label">{{ $t('event.description') }}</div>
                                <div v-if="!editDescription" class="edit copy-s bold" @click="openEditDescription">
                                    {{ $t(event.edit) }}
                                </div>
                                <div v-else class="edit save copy-s bold" @click="saveDescription">
                                    {{ $t('event.save') }}
                                </div>
                                <div v-if="!editDescription" class="edit copy-s bold" @click="editDescription = true">
                                    {{ $t('event.edit') }}
                                </div>
                            </div>
                            <div v-if="!editDescription" class="copy-m rendered" v-html="event.description" />
                            <TextAreaAutosize
                                v-else
                                v-model="event.description"
                                class="description-textarea copy-m"
                            ></TextAreaAutosize>
                        </div>
                    </div>
                    <div v-if="settings" class="settings-group">
                        <SettingsManager
                            v-if="!$apollo.queries.event.loading"
                            :settings="settings"
                            type="event"
                            :apollo-data="$apolloData.data.event"
                            @save="saveSetting"
                        />
                    </div>
                    <div v-if="reservationSettings" class="settings-group">
                        <SettingsManager
                            v-if="!$apollo.queries.event.loading"
                            :settings="reservationSettings"
                            type="event"
                            :apollo-data="$apolloData.data.event"
                            @save="saveSetting"
                        />
                    </div>
                </div>
                <div class="info-container">
                    <DateCard :value="{ startDate: event.startDate, endDate: event.endDate }" @input="updateDate" />
                    <CapacityCard
                        v-if="signedInUser"
                        :current="venueCapacityUsage"
                        :venue-i-d="currentVenueID"
                        :show-add-user="!isDraft && isActive"
                        :editable="isDraft"
                        :event="event"
                        :venues="signedInUser.venueAccess"
                        @open="selectVenueOpen = true"
                        @venueselect="(id) => selectVenue(id)"
                        @addVisitor="$apollo.queries.event.refetch()"
                    />
                    <GuestList
                        v-if="$route.name !== 'event-new'"
                        :count="eventGuestList && eventGuestList.count ? eventGuestList.count : 0"
                        @click="$router.push({ name: 'event-guest-list', params: { id: $route.params.event_id } })"
                    />
                    <div class="open-guestlist-card" @click="openGuestListModalOpen = true">
                        <p><b>Open Guest List</b></p>
                    </div>
                    <div v-if="!$apollo.queries.event.loading">
                        <Modal v-if="openGuestListModalOpen === true" @close="openGuestListModalOpen === false">
                            <div class="modal-content">
                                <div class="headline-container">
                                    <div class="copy-l bold">Generate Open Guest List</div>
                                </div>
                                <p style="max-width: 500px">
                                    Create an open guest list link to share with anybody. People with this link can
                                    enter their contact data via Web or App and will be visible on the guest list with
                                    status "Requested". You need to approve every request individually.
                                </p>
                                <br />
                                <p style="max-width: 500px">
                                    Info: Everyone with this link can request a guest list spot and will reduce the
                                    number of available spots.
                                </p>
                                <br />
                                <p style="max-width: 500px">
                                    Please enter a maximum number of requests allowed through this link.
                                </p>
                                <br />
                                <div class="main">
                                    <DInput
                                        v-model="openGuestListLimit"
                                        :placeholder="'Limit of Guests'"
                                        aria-autocomplete="none"
                                        :type="'number'"
                                    ></DInput>
                                    <div
                                        v-if="this.openGuestListLink"
                                        class="action-container fullwidth"
                                        style="margin-top: 1.5rem"
                                    >
                                        <DInput
                                            v-model="this.openGuestListLink"
                                            :placeholder="'Limit of Guests'"
                                            aria-autocomplete="none"
                                            disabled="true"
                                            style="margin-right: 1.5rem"
                                        ></DInput>
                                        <Button
                                            type="secondary"
                                            :value="'Copy Link'"
                                            @click="document.execCommand('copy')"
                                            style="height: 38px"
                                        />
                                    </div>
                                </div>
                                <div class="action-container fullwidth">
                                    <Button
                                        type="secondary"
                                        :value="$t('venue_overview.modal.cancel')"
                                        @click="openGuestListModalOpen = false"
                                    />
                                    <Button
                                        type="primary"
                                        :value="
                                            (event.openGuestList && event.openGuestList.limit > 0) || linkGenerated
                                                ? 'Update'
                                                : 'Generate'
                                        "
                                        @click="generateOpenGuestList"
                                    />
                                </div>
                            </div>
                        </Modal>
                    </div>
                    <!-- <QRCodeCard v-if="!isDraft" type="Event" @click="openQRCode"></QRCodeCard> -->
                    <div class="genre-container">
                        <div class="genre-headline">
                            <div class="label">{{ $t('event.genres') }}</div>
                            <div class="edit copy-s bold" @click="modalOpen = 'genres'">{{ $t('event.edit') }}</div>
                        </div>
                        <div class="genres">
                            <Tag v-for="genre in event.genres" :key="genre" :value="genre" />
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <Modal v-if="false">
            <ImageUpload></ImageUpload>
        </Modal>
        <div v-if="!$apollo.queries.event.loading">
            <SettingsModal
                v-if="modalOpen !== false"
                :setting="activeSetting"
                :pre-value="$apolloData.data.event[modalOpen].toString()"
                @close="closeSetting"
                @save="saveSetting"
            />
        </div>
    </div>
</template>

<script>
import Button from '@/components/Button'
import Icon from '@/components/Icon'
import FeatureImage from '@/components/FeatureImage'
import Tag from '@/components/Tag'
import FeatherIcon from '@/components/FeatherIcon'
import gql from 'graphql-tag'
import defaultSettings from '../../models/event.json'
import SettingsManager from '@/components/SettingsManager'
import SettingsModal from '@/components/SettingsModal'
import { Editor } from 'tiptap'
import CapacityCard from '@/components/CapacityCard'
import DContextMenu from '@/components/DContextMenu'
import DContextMenuItem from '@/components/DContextMenuItem'
import ImageUpload from '@/components/ImageUpload'
import Modal from '@/components/Modal'
import DateCard from '@/components/DateCard'
import TextAreaAutosize from '@/components/TextAreaAutosize'
import DLoader from '@/components/DLoader'
import GuestList from '@/components/GuestList'
import DInput from '@/components/Input'

export default {
    name: 'Event',
    components: {
        Button,
        Tag,
        Icon,
        FeatherIcon,
        FeatureImage,
        SettingsManager,
        SettingsModal,
        CapacityCard,
        DContextMenu,
        DContextMenuItem,
        ImageUpload,
        Modal,
        DInput,
        DateCard,
        TextAreaAutosize,
        DLoader,
        GuestList,
    },
    data() {
        return {
            modalOpen: false,
            openGuestListModalOpen: false,
            openGuestListLimit: '',
            openGuestListLink: '',
            linkGenerated: false,
            editor: new Editor(),
            venueID: null,
            diffSettings: [
                {
                    name: 'name',
                    type: 'text',
                    value: '',
                    description: 'Event Titel',
                    empty: '',
                    icon: '',
                },
                {
                    name: 'genres',
                    type: 'tags',
                    value: '',
                    description: 'Genres hinzufügen',
                    empty: 'Genres',
                    icon: 'tag',
                    selectOptions: [
                        { value: 'AlternativeIndie', text: 'AlternativeIndie' },
                        { value: 'Country', text: 'Country' },
                        { value: 'Dance', text: 'Dance' },
                        { value: 'EDM', text: 'EDM' },
                        { value: 'Electro', text: 'Electro' },
                        { value: 'Goa', text: 'Goa' },
                        { value: 'HipHop', text: 'HipHop' },
                        { value: 'House', text: 'House' },
                        { value: 'Jazz', text: 'Jazz' },
                        { value: 'Latin', text: 'Latin' },
                        { value: 'Metal', text: 'Metal' },
                        { value: 'Pop', text: 'Pop' },
                        { value: 'Rock', text: 'Rock' },
                        { value: 'Techno', text: 'Techno' },
                    ],
                },
                {
                    name: 'date',
                    type: 'text',
                    value: '',
                    description: 'Zeit hinzufügen',
                    empty: 'Zeit',
                    icon: '',
                },
            ],
            isDraft: true,
            editDescription: false,
            event: {
                name: '',
                description: 'Meine Eventbeschreibung',
                startDate: new Date(),
                endDate: new Date(),
                sourceAttendingCount: 0,
                sourceInterestedCount: 0,
                genres: [],
                type: 'Local',
                source: 'PartyMate',
                dresscode: null,
                minimumAge: null,
                ticketUrl: '',
                minimumBeerPrice: null,
                reservationUrl: '',
                reservationPhone: '',
                reservationEmail: '',
                reservationFee: '',
                status: 'Published',
                coverImageUrl: '',
                minimumEntrancePrice: null,
                minimumLongdrinkPrice: null,
            },
        }
    },
    apollo: {
        event: {
            query: gql`
                query ($id: ID!) {
                    event(id: $id) {
                        id
                        status
                        name
                        coverImageUrl
                        sourceAttendingCount
                        genres
                        sourceInterestedCount
                        startDate
                        endDate
                        description
                        minimumEntrancePrice
                        minimumAge
                        dresscode
                        ticketUrl
                        minimumBeerPrice
                        minimumLongdrinkPrice
                        reservationUrl
                        reservationPhone
                        reservationFee
                        reservationEmail
                        checkinEventQRCode
                        capacityInUse
                        openGuestList {
                            limit
                            counter
                        }
                        venue {
                            id
                            name
                            logoUrl
                            sourceStarRating
                            priceLevel
                            capacity
                            activeEvents {
                                id
                                capacityUsage
                                capacityInUse
                            }
                        }
                    }
                }
            `,
            variables() {
                return {
                    id: this.$route.params.event_id,
                }
            },
            skip() {
                return this.isDraft
            },
            result({ data }) {
                this.openGuestListLimit = data.event.openGuestList ? data.event.openGuestList.limit : undefined
            },
        },
        signedInUser: {
            query: gql`
                query {
                    signedInUser {
                        displayName
                        venueAccess {
                            venue {
                                id
                                name
                                logoUrl
                                priceLevel
                                capacity
                                sourceStarRating
                            }
                        }
                    }
                }
            `,
        },
        eventGuestList: {
            query: gql`
                query ($eventId: ID!) {
                    eventGuestList(eventId: $eventId) {
                        count
                    }
                }
            `,
            variables() {
                return {
                    eventId: this.$route.params.event_id,
                }
            },
        },
    },
    computed: {
        isActive() {
            let startDate = new Date(this.event.startDate).getTime()
            let endDate = new Date(this.event.endDate).getTime()
            let currentTime = Date.now()

            return currentTime > startDate && currentTime < endDate
        },
        currentVenueID() {
            if (this.isDraft) {
                return parseInt(this.venueID)
            } else {
                return this.event.venue ? this.event.venue.id : null
            }
        },
        descriptionChanged() {
            return this.event.description !== this.editor.content
        },
        venueCapacityUsage() {
            let capacityUsage = 0
            if (this.event.venue) {
                for (let activeEvent of this.event.venue.activeEvents) {
                    capacityUsage = capacityUsage + activeEvent.capacityInUse
                }
            }
            return capacityUsage
        },
        settings() {
            const settings = defaultSettings.settings
            return settings ? settings : []
        },
        reservationSettings() {
            const settings = defaultSettings.reservationSettings
            return settings ? settings : []
        },
        activeSetting() {
            return this.diffSettings.find((x) => x.name === this.modalOpen)
        },
        dateTime() {
            if (!this.event || !this.event.startDate)
                return {
                    smallDate: {
                        day: '',
                        month: '',
                    },
                    largeDate: '',
                }

            let startTime = new Date(this.event.startDate)
            let endTime = new Date(this.event.endDate)
            let locale = 'de-DE'
            return {
                smallDate: {
                    day: startTime.toLocaleDateString(locale, { day: 'numeric' }),
                    month: startTime.toLocaleDateString(locale, { month: 'short' }),
                },
                largeDate:
                    startTime.toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric' }) +
                    ', ' +
                    startTime.toLocaleDateString(locale, { weekday: 'long' }),
                largeTime:
                    startTime.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' }) +
                    ' Uhr - ' +
                    endTime.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' }) +
                    ' Uhr',
            }
        },
    },
    beforeDestroy() {
        this.editor.destroy()
    },
    mounted() {
        if (this.$route.name !== 'event-new') {
            this.isDraft = false
        }
        if (this.isDraft) {
            this.$apollo.queries.event.stop()
        }
        if (this.$route.params.venueId) {
            this.venueID = this.$route.params.venueId
        }
    },
    methods: {
        updateDate(dateTime) {
            let endDate = {
                name: 'endDate',
                type: 'text',
                value: dateTime.endDate,
            }

            let startDate = {
                name: 'startDate',
                type: 'text',
                value: dateTime.startDate,
            }

            this.saveSetting(startDate)
            this.saveSetting(endDate)
        },
        async openQRCode() {
            await this.$apollo
                .mutate({
                    mutation: gql`
                        mutation ($id: ID!) {
                            generateEventCheckinPDF(eventID: $id)
                        }
                    `,
                    variables: {
                        id: this.event.id,
                    },
                })
                .then((data) => {
                    this.downloadURI(data.data.generateEventCheckinPDF, `partymate-event-${this.event.id}.pdf`)
                })
        },
        downloadURI(uri, name) {
            let link = document.createElement('a')
            link.download = name
            link.href = uri
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
        },
        selectVenue(id) {
            this.venueID = id
        },
        async cancelEvent(event) {
            event.status = 'Canceled'

            await this.$apollo
                .mutate({
                    mutation: gql`
                        mutation ($id: ID!, $value: PartialEventInput!) {
                            updateEvent(id: $id, input: $value) {
                                status
                            }
                        }
                    `,
                    variables: {
                        id: event.id,
                        value: { status: 'Canceled' },
                    },
                })
                .catch(() => {
                    this.$pm.createToast('error', `Event konnte nicht abgesagt werden.`)
                })
        },
        async unpublishEvent() {
            this.event.status = 'Draft'
            await this.$apollo
                .mutate({
                    mutation: gql`
                        mutation ($id: ID!, $value: PartialEventInput!) {
                            updateEvent(id: $id, input: $value) {
                                status
                            }
                        }
                    `,
                    variables: {
                        id: this.event.id,
                        value: { status: 'Draft' },
                    },
                })
                .catch(() => {
                    this.$pm.createToast('error', `Event konnte nicht unveröffentlicht werden.`)
                })
        },
        async saveImage(image) {
            if (this.isDraft) {
                this.event.coverImageUrl = image
                return
            }
            await this.$apollo
                .mutate({
                    mutation: gql`
                        mutation ($id: ID!, $value: PartialEventInput!) {
                            updateEvent(id: $id, input: $value) {
                                coverImageUrl
                            }
                        }
                    `,
                    variables: {
                        id: this.event.id,
                        value: { coverImageUrl: image },
                    },
                })
                .catch(() => {
                    this.$pm.createToast('error', `Bild konnte nicht gespeichert werden.`)
                })
        },
        async publishEvent() {
            this.event.status = 'Published'
            await this.$apollo
                .mutate({
                    mutation: gql`
                        mutation ($id: ID!, $value: PartialEventInput!) {
                            updateEvent(id: $id, input: $value) {
                                status
                            }
                        }
                    `,
                    variables: {
                        id: this.event.id,
                        value: { status: 'Published' },
                    },
                })
                .catch(() => {
                    this.$pm.createToast('error', `Event konnte nicht veröffentlicht werden.`)
                })
        },
        async createEvent() {
            let data = this.event
            let venueID = this.venueID

            await this.$apollo
                .mutate({
                    mutation: gql`
                        mutation ($input: EventInput!, $venueID: ID) {
                            createEvent(input: $input, venueID: $venueID) {
                                id
                                name
                                description
                                genres
                                startDate
                                type
                                source
                                status
                                page {
                                    id
                                }
                            }
                        }
                    `,
                    variables: {
                        input: data,
                        venueID: venueID,
                    },
                })
                .then((data) => {
                    this.$router.push(
                        {
                            name: 'event',
                            params: {
                                // id: data.data.createEvent.page.id,
                                event_id: data.data.createEvent.id,
                            },
                        },
                        () => {
                            this.$router.go()
                        },
                    )
                })
                .catch((e) => {
                    console.log(e)
                    this.$pm.createToast('error', `Event konnte nicht erstellt werden.`)
                })
        },
        saveDescription() {
            this.editDescription = false

            let setting = {
                name: 'description',
                value: this.event.description,
            }

            this.saveSetting(setting)
        },
        openEditDescription() {
            this.editDescription = true
        },
        closeSetting() {
            this.modalOpen = false
        },
        async generateOpenGuestList() {
            await this.$apollo
                .mutate({
                    mutation: gql`
                        mutation ($eventId: ID!, $limit: Int!) {
                            syncOpenEventGuestList(eventId: $eventId, limit: $limit) {
                                link
                            }
                        }
                    `,
                    variables: {
                        eventId: this.event.id,
                        limit: parseInt(this.openGuestListLimit),
                    },
                })
                .then((data) => {
                    this.openGuestListLink = data.data.syncOpenEventGuestList.link
                    this.linkGenerated = true
                })
                .catch((e) => {
                    console.log(e)
                    this.$pm.createToast('error', `Something went wrong.`)
                })
        },
        async saveSetting(setting) {
            let name = setting.name

            if (this.isDraft) {
                this.event[name] = setting.value
            } else {
                let eventInput = {}
                eventInput[name] = setting.value

                let eventId = this.$route.params.event_id

                await this.$apollo
                    .mutate({
                        mutation: gql`mutation($id: ID!, $value: PartialEventInput!) {
                          updateEvent(id: $id, input: $value) {
                            ${name}
                          }
                        }
                        `,
                        variables: {
                            id: eventId,
                            value: eventInput,
                        },
                    })
                    .catch(() => {
                        this.$pm.createToast('error', `Einstellung konnte nicht gespeichert werden.`)
                    })
            }

            this.modalOpen = false
            this.event[name] = setting.value
        },
        getDescription() {
            return this.event.description
        },
    },
}
</script>

<style scoped lang="sass">
.loader
    display: flex
    justify-content: center

.more-horizontal
    z-index: 5
    width: 3.2rem
    height: 3.2rem
    background: $gray-1
    display: flex
    align-items: center
    justify-content: center
    border-radius: 99em
    margin-left: $s
    +transition(80ms)

    &:hover
        background: darken($gray-1, 5%)

    .feather-icon
        width: 1.8rem

.edit
    cursor: pointer

.event
    .sub-nav
        display: flex
        justify-content: space-between
        margin-bottom: $s
        @include breakpoint('m')
            display: grid
            grid-template-columns: 1fr
            grid-gap: 12px
            margin-bottom: 18px

        &-right
            display: flex
            justify-content: flex-end

            .button + .button
                margin-left: $s

.draft-banner
    background: $neutral-black
    padding: 0 $l
    height: 6.8em
    border-radius: $s
    display: flex
    align-items: center
    margin: $l 0

    .icon
        padding-right: $m

    .text-container
        .copy-m
            margin-bottom: $xxxs

        p
            color: $neutral-white

.main-content
    margin-top: $xl
    margin-bottom: $xxl
    display: grid
    grid-gap: $l
    grid-template-columns: 520px auto

    .open-guestlist-card
        background: $neutral-white
        box-shadow: $box-mois
        display: flex
        justify-content: space-between
        border-radius: 10px
        align-items: center
        margin-bottom: $l
        cursor: pointer
        padding: $m

    @include breakpoint('l')
        max-width: 100%
        overflow: hidden
        grid-template-columns: 1fr

    .settings-container
        box-sizing: border-box

        .settings-group
            max-width: 100%
            border-radius: $s
            padding: $s
            background: $neutral-white
            box-shadow: $box-mois

        .event-name-container
            display: flex
            justify-content: space-between
            padding: $s $m

            .edit
                color: $neutral-2

            .rsvp-container
                display: flex
                align-items: center
                margin-top: $xxxs

                .text
                    white-space: nowrap
                    text-align: right

                .user-icon.feather-icon
                    width: 16px
                    padding-right: $xs

        .event-desc-container
            border-top: 1px $neutral-1 solid
            padding: $m $m $s $m

            .description-textarea
                width: 100%
                border: none
                background: $gray-2
                border-radius: 8px
                padding: $xxs
                box-sizing: border-box

            .rendered ::v-deep p
                min-height: 1.8rem

            .copy-m
                white-space: pre-wrap

            .edit
                color: $neutral-2

                &.save
                    color: $primary-1

            .label-container
                display: flex
                justify-content: space-between
                margin-bottom: $s

                p
                    color: $neutral-2
                    margin-top: $xs

    .info-container
        box-sizing: border-box

        .genre-container
            .genre-headline
                display: flex
                justify-content: space-between
                margin-bottom: $s

            .genres
                .tag
                    margin-left: 0
                    margin-bottom: $xs
                    margin-right: $xs

        .qr-code-card,
        .capacity-card
            margin-bottom: $l

        .calendar-container
            background: $neutral-white
            box-shadow: $box-mois
            padding: $s $l $s $s
            display: flex
            justify-content: space-between
            border-radius: 10px
            align-items: center
            margin-bottom: $l

            .date-container
                display: flex
                align-items: center

            .small-date
                width: 64px
                height: 64px
                display: flex
                align-items: center
                flex-direction: column
                justify-content: center
                margin-right: $m

                .label
                    color: $red

            .large-date
                p:first-child
                    margin-bottom: $xxs

            .feather-icon
                width: 2em
                height: 2em
                color: $neutral-2
</style>
